import i18n from 'i18next';
import type { FormItemDefinition, RecordFormItem } from 'types';
import { VISIBILITY_STATE } from 'types/enums';
import { EMAIL_REGEX } from 'utils/regex';
import { toLowerCamelCase } from 'utils/string';
import DOMPurify from 'dompurify';
import { array, mixed, number, string } from 'yup';

const getPlainText = (value?: string) => {
  return value && DOMPurify.sanitize(value, { ALLOWED_TAGS: [] });
};

export const recordItemsValidation = (items: RecordFormItem[], skipValidation: boolean) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items.reduce<Record<string, any>>((acc, { formItem, visibilityState }) => {
    if (visibilityState === VISIBILITY_STATE.HIDDEN) {
      acc[formItem.id] = mixed();
    } else {
      // When validation is skipped (e.g. autosave/change-behavior saves), required checks are
      // relaxed but the maxLength limit must still be enforced (F20-3967).
      acc[formItem.id] = getRecordItemValidation(formItem.definition, skipValidation);
    }

    return acc;
  }, {});

export const getRecordItemValidation = (definition: FormItemDefinition, skipRequired = false) => {
  const key = toLowerCamelCase(definition.type) as keyof FormItemDefinition;
  const formItemConfig = definition[key] as { isRequired?: boolean; maxLength?: number };

  let validation = getValidationByFieldType(definition.type, formItemConfig, skipRequired);

  if (!skipRequired && formItemConfig?.isRequired) {
    validation = ['Records', 'Tables', 'Users', 'FileUpload'].includes(definition.type)
      ? validation?.min(1, i18n.t('common.required'))
      : validation?.required(i18n.t('common.required'));
  }

  if (definition.type !== 'RichText' && formItemConfig?.maxLength) {
    validation = validation?.max(formItemConfig.maxLength, i18n.t('common.maxLength') + ': ' + formItemConfig.maxLength);
  }

  return validation;
};

interface FormItemConfig {
  maxLength?: number;
  isRequired?: boolean;
}

export const getValidationByFieldType = (fieldType: string, formItemConfig?: FormItemConfig, skipRequired = false) => {
  switch (fieldType) {
    case 'Text':
    case 'RadioButton':
    case 'PhoneNumber':
    case 'Date':
      return string();

    case 'RichText':
      return string()
        .test('plainTextLength', i18n.t('common.maxLength') + ': ' + formItemConfig?.maxLength, (value) => {
          if (!formItemConfig?.maxLength) return true;
          const plainTextLength = (getPlainText(value) ?? '').length;
          return plainTextLength <= formItemConfig.maxLength;
        })
        .test('plainTextRequired', i18n.t('common.required'), (value) => {
          if (skipRequired || !formItemConfig?.isRequired) return true;
          const plainText = getPlainText(value);
          return (plainText?.trim() ?? '').length > 0;
        });

    case 'Email':
      return string()
        .nullable()
        .email(i18n.t('common.invalidEmail'))
        .matches(EMAIL_REGEX, i18n.t('common.invalidEmail'))
        .transform((value) => (value === '' ? null : value));

    case 'Number':
      return number()
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value));

    case 'FileUpload':
    case 'Users':
    case 'Tables':
    case 'Records':
      return array();

    default:
      return undefined;
  }
};
